import { Configuration, FrontendApi, Session } from '@ory/client'
import * as Sentry from '@sentry/vue'
import { StorageSerializers, useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

import { useRouter } from 'vue-router'

import {
  AccessTokenService,
  AvailableCompany,
  CompanyAccessRole,
  CompanyData,
  CompanyManagementService,
  OpenAPI,
  UserProfile,
  UserProfileService,
  CompanyIdentityAccessWithUserProfile,
} from '@/client'
import { resetDatabase } from './database'
import { notificationStore } from './notification.store'
import { CacheStateRepository } from './cacheState.store'
import { _resetBackgroundjobCache } from './backgroundjob/backgroundjob.utils'

export enum SessionStatus {
  LOGGED_OUT = 'LOGGED_OUT',
  LOGGED_IN = 'LOGGED_IN',
}

export type CompanyRegion = 'EU' | 'US'

type AvailableCompanyWithToken = AvailableCompany & {
  token: string
  tokenType: string | undefined // Bearer
  tokenExpiresAt?: number | null
  region?: CompanyRegion
}

export const useSessionStore = defineStore('session', () => {
  // State
  // Ory related
  const status = ref<SessionStatus>(SessionStatus.LOGGED_OUT)
  const currentCompany = useStorage<null | AvailableCompanyWithToken>(
    'currentCompany',
    null,
    undefined,
    { serializer: StorageSerializers.object }
  )
  const currentUser = ref<null | UserProfile>(null)
  const currentOrySession = ref<null | Session>(null)
  const userCompanyAccessRole = ref<null | CompanyAccessRole>(null)
  const companyMembers = ref<CompanyIdentityAccessWithUserProfile[]>([])

  const router = useRouter()

  const oryUrl = computed(() => {
    switch (import.meta.env.MODE) {
      case 'production':
        return 'https://auth.saasmetrix.io'
      case 'staging':
        return 'https://auth.saasmetrix.io'
      case 'staging2':
        return 'https://auth.saasmetrix.io'
      case 'beta':
        return 'https://auth.saasmetrix.io'
      case 'syss':
        return 'https://auth.saasmetrix.io'
      default:
        return 'http://127.0.0.1:4000'
    }
  })

  const frontend = new FrontendApi(
    new Configuration({
      basePath: oryUrl.value,
      baseOptions: {
        withCredentials: true,
      },
    })
  )

  // saasmetrix related
  const currentCompanyInfo = ref<null | CompanyData>(null)
  let tokenRefreshTimeout: ReturnType<typeof setTimeout> | null = null
  let isRefreshingToken = false
  let lastRefreshAttempt = 0
  const MIN_REFRESH_BUFFER_MS = 5_000
  const MAX_REFRESH_BUFFER_MS = 60_000
  const MIN_RETRY_DELAY_MS = 5_000

  async function resetCachesForCompanyChange(nextCompanyId: string) {
    const currentCompanyId = currentCompany.value?.company_id ?? null
    const cachedCompanyId = (
      await CacheStateRepository.getCacheStateById('companyId')
    )?.companyId

    const hasCompanyChanged =
      (currentCompanyId != null && currentCompanyId !== nextCompanyId) ||
      (cachedCompanyId != null && cachedCompanyId !== nextCompanyId)

    if (!hasCompanyChanged) {
      return
    }

    await resetDatabase()
    await _resetBackgroundjobCache()
  }

  // ************************************************************
  // Company related
  // ************************************************************

  // Login to a company
  async function loginToCompany(
    company: AvailableCompany & { region?: CompanyRegion }
  ) {
    const token = await AccessTokenService.requestTokenTokenPost({
      requestBody: { company_id: company.company_id },
    })
    const tokenExpiresAt = extractTokenExpiry(token.access_token)

    // Reset and reload all Caches
    await resetCachesForCompanyChange(company.company_id)
    notificationStore.resetNovuInstance()
    companyMembers.value = []
    userCompanyAccessRole.value = null
    currentCompanyInfo.value = null

    await CacheStateRepository.upsertCacheState({
      id: 'companyId',
      companyId: company.company_id,
    })

    // Set JWT in session storage
    currentCompany.value = {
      ...company,
      company_id: company.company_id,
      token: token.access_token,
      tokenType: token.token_type,
      tokenExpiresAt,
      region: company.region,
    }

    // Ensure API client uses the freshly issued token immediately
    OpenAPI.TOKEN = token.access_token

    // Clear stale company info until fresh data arrives
    await fetchCompanyInfo().catch(() => {
      currentCompanyInfo.value = null
    })

    Sentry.setUser({
      email: currentOrySession.value?.identity?.traits.email,
      id: currentOrySession.value?.identity?.id,
      company_id: currentCompany.value?.company_id || null,
      company_name: currentCompany.value?.name || null,
    })

    return token
  }

  // Watch for changes in currentCompany and update the JWT
  watch(
    currentCompany,
    (currentCompany) => {
      clearScheduledTokenRefresh()
      if (currentCompany != null) {
        if (!currentCompany.tokenExpiresAt) {
          currentCompany.tokenExpiresAt = extractTokenExpiry(
            currentCompany.token
          )
        }
        OpenAPI.TOKEN = currentCompany.token
        scheduleTokenRefresh(currentCompany)
      } else {
        OpenAPI.TOKEN = ''
      }
    },
    { immediate: true }
  )

  // Fetch saasmetrix company info
  async function fetchCompanyInfo() {
    return await CompanyManagementService.getCompanyInfoApiV1CompanyInfoGet(
      {}
    ).then((companyInfo) => {
      currentCompanyInfo.value = companyInfo
      return companyInfo
    })
  }

  async function getCompanyMembers() {
    if (companyMembers.value.length > 0) {
      return companyMembers.value
    }

    await CompanyManagementService.getCompanyIdentityAccessesWithUserProfileApiV1CompanyIdentityAccessesGet(
      {}
    ).then((res) => {
      companyMembers.value = res
    })

    return companyMembers.value
  }

  // ************************************************************
  // ORY Kratos Session
  // ************************************************************

  // Redirects to the login page if the user is not logged in
  async function requireLogin(reloadSession = true) {
    if (reloadSession) await checkSession()

    // Redirect to login page if not logged in
    if (status.value === SessionStatus.LOGGED_OUT) {
      // Do not redirect to login page if already on login page
      if (window.location.pathname !== '/login') window.location.href = '/login'
    }

    return status.value === SessionStatus.LOGGED_IN
  }

  // Gets the current session if it exists, otherwise returns null
  async function checkSession() {
    let currentSession = null
    await frontend
      .toSession()
      .then((session) => {
        if (session.data.active) {
          currentOrySession.value = session.data

          status.value = SessionStatus.LOGGED_IN
          currentSession = session.data
        } else {
          status.value = SessionStatus.LOGGED_OUT
        }
      })
      .catch((error) => {
        // If two factor is required, redirect to login page
        if (
          error.response.status === 403 &&
          error.response.data.error.id === 'session_aal2_required'
        ) {
          router.push('/login?mode=otp')
        }

        status.value = SessionStatus.LOGGED_OUT
      })

    return currentSession
  }

  // ************************************************************
  // User
  // ************************************************************

  // When logged in load user
  watch(
    [status, () => currentCompany.value?.company_id],
    async ([nextStatus, currentCompanyId]) => {
      if (nextStatus === SessionStatus.LOGGED_IN && currentCompanyId) {
        await get_user()
        return
      }

      if (!currentCompanyId) {
        currentUser.value = null
      }
    },
    { immediate: true }
  )

  async function get_user() {
    currentUser.value = await UserProfileService.getUserProfileUserGet({})

    // Set sentry user
    Sentry.setUser({
      email: currentOrySession.value?.identity?.traits.email,
      id: currentOrySession.value?.identity?.id,
      company_id: currentCompany.value?.company_id || null,
      company_name: currentCompany.value?.name || null,
    })

    return currentUser.value
  }

  function switchCompany() {
    currentCompany.value = null
    currentCompanyInfo.value = null
    router.push('/login/company')
  }

  // ************************************************************
  // Logout
  // ************************************************************

  async function logout() {
    const logoutFlow = await frontend.createBrowserLogoutFlow()

    await frontend.updateLogoutFlow({
      token: logoutFlow.data.logout_token,
    })

    // Delete the current company
    currentCompany.value = null
    currentCompanyInfo.value = null
    currentUser.value = null
    clearScheduledTokenRefresh()
    isRefreshingToken = false
    status.value = SessionStatus.LOGGED_OUT
    localStorage.removeItem('preferences')

    // Empty the database when logging out
    await resetDatabase()
    await _resetBackgroundjobCache()

    // Redirect to login page
    router.push('/login')
  }

  // ************************************************************
  // CompanyAccessRole, Scopes
  // ************************************************************

  async function loadUserCompanyAccessRole() {
    const companyAccesses =
      await CompanyManagementService.getCompanyIdentityAccessesWithUserProfileApiV1CompanyIdentityAccessesGet(
        {}
      )

    // Check if user is loaded
    if (currentUser.value == null) {
      await get_user()
    }

    // Find company access
    const companyAccess = companyAccesses.find(
      (companyAccess) => companyAccess._id === currentUser.value?._id
    )

    // Set company access role
    if (companyAccess) {
      userCompanyAccessRole.value = companyAccess.company_access.role
    } else {
      console.error('Could not find company access')
    }
  }

  async function hasPermission(permission: CompanyAccessRole) {
    // Load company access role if not loaded
    if (userCompanyAccessRole.value == null) {
      await loadUserCompanyAccessRole()
    }

    // Check if user has permission
    if (userCompanyAccessRole.value == null) return false

    // Order of permissions
    const permissions = [
      CompanyAccessRole.ADMIN,
      CompanyAccessRole.ADMIN_READ_ONLY,
      CompanyAccessRole.MEMBER,
      CompanyAccessRole.SUSPENDED,
    ]

    // Check if user has permission
    return (
      permissions.indexOf(userCompanyAccessRole.value) <=
      permissions.indexOf(permission)
    )
  }

  async function refreshCompanyToken(force = false) {
    if (isRefreshingToken || !currentCompany.value) return
    const now = Date.now()
    const timeSinceLastAttempt = now - lastRefreshAttempt
    if (!force && timeSinceLastAttempt < MIN_RETRY_DELAY_MS) {
      scheduleTokenRefresh(
        currentCompany.value,
        MIN_RETRY_DELAY_MS - timeSinceLastAttempt
      )
      return
    }
    isRefreshingToken = true
    lastRefreshAttempt = now
    try {
      const token = await AccessTokenService.requestTokenTokenPost({
        requestBody: { company_id: currentCompany.value.company_id },
      })
      const tokenExpiresAt = extractTokenExpiry(token.access_token)
      currentCompany.value = {
        ...currentCompany.value,
        token: token.access_token,
        tokenType: token.token_type,
        tokenExpiresAt,
      }
      OpenAPI.TOKEN = token.access_token
      if (currentCompany.value) {
        scheduleTokenRefresh(currentCompany.value)
      }
    } catch (error) {
      console.error('Failed to refresh company token', error)
      if (currentCompany.value) {
        scheduleTokenRefresh(currentCompany.value, MIN_RETRY_DELAY_MS)
      }
    } finally {
      isRefreshingToken = false
    }
  }

  function scheduleTokenRefresh(
    company: AvailableCompanyWithToken,
    overrideDelay?: number
  ) {
    clearScheduledTokenRefresh()
    if (typeof window === 'undefined') return
    if (overrideDelay && overrideDelay > 0) {
      tokenRefreshTimeout = setTimeout(() => {
        refreshCompanyToken()
      }, overrideDelay)
      return
    }

    const expiresAt = company.tokenExpiresAt
    if (!expiresAt) return

    const remainingMs = expiresAt * 1000 - Date.now()
    if (remainingMs <= 0) {
      refreshCompanyToken(true)
      return
    }

    const dynamicBuffer = Math.max(
      MIN_REFRESH_BUFFER_MS,
      Math.min(MAX_REFRESH_BUFFER_MS, Math.floor(remainingMs * 0.2))
    )
    const delay = remainingMs - dynamicBuffer

    if (delay <= 0) {
      refreshCompanyToken(true)
      return
    }

    tokenRefreshTimeout = setTimeout(() => {
      refreshCompanyToken()
    }, delay)
  }

  function clearScheduledTokenRefresh() {
    if (tokenRefreshTimeout) {
      clearTimeout(tokenRefreshTimeout)
      tokenRefreshTimeout = null
    }
  }

  function extractTokenExpiry(token: string): number | null {
    try {
      if (typeof window === 'undefined' || typeof window.atob !== 'function') {
        return null
      }
      const payload = token.split('.')[1]
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
      const padded = normalized.padEnd(
        normalized.length + ((4 - (normalized.length % 4)) % 4),
        '='
      )
      const decoded = JSON.parse(window.atob(padded))
      if (typeof decoded.exp === 'number') {
        return decoded.exp
      }
    } catch (error) {
      console.warn('Failed to decode token expiry', error)
    }
    return null
  }

  //   Return
  return {
    status,
    frontend,
    currentUser,
    companyMembers,
    currentOrySession,

    // Company (saasmetrix)
    currentCompanyInfo,
    currentCompany,

    // Actions
    requireLogin,
    checkSession,
    get_user,
    fetchCompanyInfo,
    getCompanyMembers,
    loadUserCompanyAccessRole,

    switchCompany,
    loginToCompany,
    logout,

    // CompanyAccessRole
    userCompanyAccessRole,
    hasPermission,
  }
})
